import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { io } from "socket.io-client";
import axios from "axios";

const API_BASE = "https://eluvent.com";
const SOCKET_URL = "https://eluvent.com";

const EMOJI_LIST = [
  "😀","😂","🤣","😊","😍","🤔","😎","😢","😡","👍","👎","🎉",
  "🔥","❤️","💡","📚","✏️","✅","❌","⭐","💬","🙏","🤗","😇"
];

const LetsTalk = () => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [showEmoji, setShowEmoji] = useState(false);
  const socketRef = useRef(null);
  const messagesEndRef = useRef(null);

  // Check auth on mount
  useEffect(() => {
    axios.get(`${API_BASE}/api/auth/user`, { withCredentials: true })
      .then(res => {
        if (res.data && res.data.id) {
          setUser(res.data);
        }
      })
      .catch(() => {})
      .finally(() => setAuthLoading(false));
  }, []);

  useEffect(() => {
    axios.get(`${API_BASE}/api/chat/messages`, { withCredentials: true })
      .then(res => setMessages(res.data.messages || []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    const socket = io(SOCKET_URL, {
      transports: ["websocket", "polling"],
      withCredentials: true
    });
    socketRef.current = socket;

    socket.on("new_message", (data) => {
      setMessages(prev => [...prev, data]);
    });

    socket.on("message_deleted", (data) => {
      setMessages(prev => prev.filter(m => m.id !== data.id));
    });

    return () => {
      socket.disconnect();
    };
  }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const sendMessage = () => {
    const text = input.trim();
    if (!text || !socketRef.current) return;
    socketRef.current.emit("send_message", { text });
    setInput("");
    setShowEmoji(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const deleteMessage = (msgId) => {
    if (!socketRef.current) return;
    socketRef.current.emit("delete_message", { id: msgId });
  };

  const isAdmin = user && user.email === "admin@eluvent.com";

  if (authLoading) {
    return (
      <div style={{ textAlign: "center", padding: 60, fontFamily: "Nunito, sans-serif" }}>
        <p>Loading...</p>
      </div>
    );
  }

  if (!user) {
    return (
      <div style={{ maxWidth: 560, margin: "60px auto", textAlign: "center", fontFamily: "Nunito, sans-serif" }}>
        <h2>🔐 Let's Talk</h2>
        <p>Please sign in to join the chat.</p>
        <a
          href={`https://eluvent.com/auth/google/login?next=/lets-talk`}
          style={{
            display: "inline-block",
            padding: "12px 24px",
            background: "#fca311",
            color: "white",
            borderRadius: 30,
            fontWeight: 700,
            textDecoration: "none",
            marginTop: 16
          }}
        >
          Sign in with Google
        </a>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 700, margin: "20px auto", fontFamily: "Nunito, sans-serif", background: "white", borderRadius: 20, boxShadow: "0 4px 20px rgba(0,0,0,0.08)", overflow: "hidden", display: "flex", flexDirection: "column", height: "80vh" }}>
      {/* Navigation bar with Logout */}
      <div style={{ background: "#f8fafc", padding: "8px 12px", borderBottom: "1px solid #e2e8f0", display: "flex", gap: 10, alignItems: "center", fontSize: 13, flexWrap: "wrap" }}>
        <Link to="/" style={{ color: "#6366f1", textDecoration: "none", fontWeight: 600 }}>🏠 Home</Link>
        <Link to="/board-vault" style={{ color: "#6366f1", textDecoration: "none", fontWeight: 600 }}>📚 Board Vault</Link>
        <Link to="/exam-predictor" style={{ color: "#6366f1", textDecoration: "none", fontWeight: 600 }}>🔮 Exam Predictor</Link>
        <Link to="/premium-simulator" style={{ color: "#7c3aed", textDecoration: "none", fontWeight: 700 }}>🌟 Premium Simulator</Link>
        <span style={{ marginLeft: "auto", fontSize: 12 }}>
          👋 {user?.name || user?.email || "Student"}
          {isAdmin && <span style={{ marginLeft: 4, background: "#ede9fe", borderRadius: 8, padding: "2px 8px", fontSize: 10, color: "#6d28d9" }}>Teacher</span>}
        </span>
        <button
          onClick={() => {
            axios.get(`${API_BASE}/auth/logout`, { withCredentials: true })
              .then(() => setUser(null))
              .catch(() => setUser(null));
          }}
          style={{
            background: "transparent",
            border: "1px solid #e2e8f0",
            borderRadius: 20,
            padding: "4px 12px",
            fontSize: 11,
            cursor: "pointer",
            color: "#64748b",
            fontWeight: 600
          }}
        >
          Logout
        </button>
      </div>

      {/* Header */}
      <div style={{ background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)", color: "white", padding: "12px 20px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <h2 style={{ margin: 0, fontSize: 20, fontWeight: 800 }}>💬 Let's Talk</h2>
        <span style={{ fontSize: 13, opacity: 0.9 }}>
          {user?.name || user?.email || "Student"}
          {isAdmin && <span style={{ marginLeft: 8, background: "rgba(255,255,255,0.2)", borderRadius: 12, padding: "2px 10px", fontSize: 11 }}>Teacher</span>}
        </span>
      </div>

      {/* Messages */}
      <div style={{ flex: 1, overflowY: "auto", padding: "16px", background: "#f8fafc" }}>
        {messages.length === 0 && (
          <p style={{ textAlign: "center", color: "#94a3b8", marginTop: 40 }}>
            No messages yet. Start the conversation! 🎉
          </p>
        )}
        {messages.map((msg) => (
          <div
            key={msg.id}
            style={{
              display: "flex",
              justifyContent: msg.email === user?.email ? "flex-end" : "flex-start",
              marginBottom: 12
            }}
          >
            <div style={{
              maxWidth: "75%",
              background: msg.email === user?.email ? "#ede9fe" : "white",
              borderRadius: 16,
              padding: "10px 14px",
              boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
              border: "1px solid #e2e8f0"
            }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
                <span style={{ fontSize: 12, fontWeight: 700, color: "#6d28d9" }}>
                  {msg.name || msg.email?.split("@")[0] || "Unknown"}
                </span>
                {isAdmin && (
                  <button
                    onClick={() => deleteMessage(msg.id)}
                    style={{
                      background: "transparent",
                      border: "none",
                      cursor: "pointer",
                      fontSize: 14,
                      color: "#ef4444",
                      padding: 0,
                      marginLeft: 8
                    }}
                    title="Delete message"
                  >
                    🗑️
                  </button>
                )}
              </div>
              <p style={{ margin: 0, fontSize: 14, color: "#1e293b", whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                {msg.text}
              </p>
              <span style={{ fontSize: 10, color: "#94a3b8", display: "block", marginTop: 4 }}>
                {new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              </span>
            </div>
          </div>
        ))}
        <div ref={messagesEndRef} />
      </div>

      {/* Input area */}
      <div style={{ borderTop: "1px solid #e2e8f0", padding: "12px 16px", background: "white", position: "relative" }}>
        {showEmoji && (
          <div style={{
            position: "absolute",
            bottom: 70,
            left: 16,
            background: "white",
            border: "1px solid #e2e8f0",
            borderRadius: 12,
            padding: 8,
            display: "grid",
            gridTemplateColumns: "repeat(8, 1fr)",
            gap: 6,
            boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
            zIndex: 10
          }}>
            {EMOJI_LIST.map(emoji => (
              <button
                key={emoji}
                onClick={() => { setInput(input + emoji); setShowEmoji(false); }}
                style={{ background: "transparent", border: "none", cursor: "pointer", fontSize: 22, padding: 4 }}
              >
                {emoji}
              </button>
            ))}
          </div>
        )}
        <div style={{ display: "flex", gap: 8, alignItems: "flex-end" }}>
          <button
            onClick={() => setShowEmoji(!showEmoji)}
            style={{
              background: "#f1f5f9",
              border: "1px solid #e2e8f0",
              borderRadius: 12,
              padding: "8px 12px",
              cursor: "pointer",
              fontSize: 18
            }}
          >
            😊
          </button>
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Type your message…"
            rows={2}
            style={{
              flex: 1,
              padding: "10px 14px",
              borderRadius: 16,
              border: "2px solid #e2e8f0",
              fontFamily: "Nunito, sans-serif",
              fontSize: 14,
              resize: "none",
              outline: "none",
              background: "#f8fafc"
            }}
          />
          <button
            onClick={sendMessage}
            style={{
              background: "linear-gradient(135deg, #667eea, #764ba2)",
              color: "white",
              border: "none",
              borderRadius: 16,
              padding: "10px 18px",
              fontWeight: 700,
              fontSize: 14,
              cursor: "pointer",
              whiteSpace: "nowrap"
            }}
          >
            Send ➤
          </button>
        </div>
      </div>
    </div>
  );
};

export default LetsTalk;